import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Download, Calendar, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';

const MonitorHistory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [monitor, setMonitor] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [timeRange, setTimeRange] = useState('24h');

  const timeRangeOptions = [
    { value: '24h', label: 'Last 24 Hours', hours: 24 },
    { value: '7d', label: 'Last 7 Days', days: 7 },
    { value: '30d', label: 'Last 30 Days', days: 30 },
    { value: '90d', label: 'Last 90 Days', days: 90 },
    { value: '180d', label: 'Last 180 Days', days: 180 }
  ];

  useEffect(() => {
    fetchMonitor();
  }, [id]);

  useEffect(() => {
    if (monitor) {
      fetchHistory();
    }
  }, [timeRange, monitor]);

  const fetchMonitor = async () => {
    try {
      const response = await api.get(`/monitors/${id}`);
      setMonitor(response.data);
    } catch (error) {
      toast.error('Failed to load monitor');
      navigate('/uptime');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const selectedRange = timeRangeOptions.find(r => r.value === timeRange);
      const params = selectedRange.hours 
        ? `hours=${selectedRange.hours}` 
        : `days=${selectedRange.days}`;
      
      const response = await api.get(`/monitors/${id}/history?${params}`);
      setHistory(response.data.history || []);
    } catch (error) {
      toast.error('Failed to load history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const formatChartData = () => {
    return history.map(item => {
      const date = new Date(item.timestamp);
      return {
        time: timeRange === '24h' 
          ? date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit' }),
        responseTime: item.response_time || 0,
        status: item.status === 'up' ? 1 : 0,
        fullTime: date.toLocaleString()
      };
    });
  };

  const calculateStats = () => {
    if (history.length === 0) return { avg: 0, min: 0, max: 0, uptime: 100 };
    
    const responseTimes = history.filter(h => h.response_time).map(h => h.response_time);
    const upCount = history.filter(h => h.status === 'up').length;
    
    return {
      avg: Math.round(responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length),
      min: Math.min(...responseTimes),
      max: Math.max(...responseTimes),
      uptime: ((upCount / history.length) * 100).toFixed(2)
    };
  };

  const exportToCSV = () => {
    const csv = [
      ['Timestamp', 'Status', 'Response Time (ms)'],
      ...history.map(h => [
        new Date(h.timestamp).toISOString(),
        h.status,
        h.response_time || 0
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `monitor-${monitor.name}-history.csv`;
    a.click();
    toast.success('History exported!');
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!monitor) return null;

  const stats = calculateStats();

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/uptime/${id}`)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Monitor
          </Button>

          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-800 mb-2">Performance History</h1>
              <p className="text-slate-600">{monitor.name} - {monitor.target}</p>
              <div className="flex items-center space-x-2 mt-2">
                <Badge variant="outline" className="uppercase">{monitor.monitoring_type}</Badge>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="w-48">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timeRangeOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={exportToCSV}
                disabled={history.length === 0}
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-slate-600">Uptime</p>
              <p className="text-2xl font-bold text-green-600">{stats.uptime}%</p>
              <p className="text-xs text-slate-500 mt-1">{history.length} checks</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-slate-600">Avg Response</p>
              <p className="text-2xl font-bold text-slate-800">{stats.avg}ms</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-slate-600">Min Response</p>
              <p className="text-2xl font-bold text-blue-600">{stats.min}ms</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-slate-600">Max Response</p>
              <p className="text-2xl font-bold text-red-600">{stats.max}ms</p>
            </CardContent>
          </Card>
        </div>

        {/* Historical Chart */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              <CardTitle>Response Time Over Time</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {loadingHistory ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-500">No history data available for this time range.</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={formatChartData()}>
                  <defs>
                    <linearGradient id="colorResponseHistory" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="time" 
                    stroke="#64748b"
                    style={{ fontSize: '11px' }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    stroke="#64748b"
                    style={{ fontSize: '12px' }}
                    label={{ value: 'Response Time (ms)', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '12px'
                    }}
                    formatter={(value, name) => {
                      if (name === 'responseTime') return [`${value}ms`, 'Response Time'];
                      if (name === 'status') return [value === 1 ? 'Up ✓' : 'Down ✗', 'Status'];
                      return [value, name];
                    }}
                  />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="responseTime" 
                    stroke="#3b82f6" 
                    fillOpacity={1}
                    fill="url(#colorResponseHistory)"
                    name="Response Time (ms)"
                  />
                  <Line 
                    type="stepAfter" 
                    dataKey="status" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    dot={false}
                    name="Status (Up=1, Down=0)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* History Table */}
        <Card>
          <CardHeader>
            <CardTitle>Check History</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingHistory ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              </div>
            ) : history.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No history records found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Timestamp</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Response Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.slice().reverse().map((record, idx) => (
                      <tr key={record.id || idx} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 text-sm text-slate-700">
                          {new Date(record.timestamp).toLocaleString()}
                        </td>
                        <td className="py-3 px-4">
                          <Badge 
                            className={record.status === 'up' 
                              ? 'bg-green-100 text-green-700 border-green-200' 
                              : 'bg-red-100 text-red-700 border-red-200'
                            }
                          >
                            {record.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-700">
                          {record.response_time ? `${record.response_time}ms` : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MonitorHistory;
